import {
  OrderType,
  SearchLearningObjectiveStatementsDocument,
  useDeleteLearningObjectiveStatementsMutation,
  useSearchLearningObjectiveStatementsQuery,
  LearningObjectiveStatementStatus,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useNavigate } from '@exam-management/router';
import {
  Alert,
  ConfirmationModal,
  DescriptionOutlinedIcon,
  Flex,
  Loading,
  NoRowsOverlay,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo, useState } from 'react';

import { LOSCollapse, LOSQuestionCountChip } from '../../components';
import { getLevelItemsForLOS } from '../../utils';
import { AddLOSFormModal } from '../add-los-form-modal';
import { DisableLOSModal } from '../disable-los-modal';
import { EditLOSFormModal } from '../edit-los-form-modal';
import { LOSListRowActionMenu } from '../los-list-row-action-menu';

export default function LOSCollapseList() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { qBankStructureInformation, qBankStructureLevelItems } = useQBankStructure();
  const { id: qBankStructureId, status } = qBankStructureInformation;

  const [isAddLOSModalOpen, addLOSModalHandler] = useDisclosure();
  const { open: openAddLOSModal, close: closeAddLOSModal } = addLOSModalHandler;
  const [isDeleteLOSModalOpen, deleteLOSModalHandler] = useDisclosure();
  const { open: openDeleteLOSModal, close: closeDeleteLOSModal } = deleteLOSModalHandler;

  const [isEditLOSModalOpen, editLOSModalHandler] = useDisclosure();
  const { open: openEditLOSModal, close: closeEditLOSModal } = editLOSModalHandler;

  const [isDisableLOSModalOpen, disableLOSModalHandler] = useDisclosure();
  const { open: openDisableLOSModal, close: closeDisableLOSModal } = disableLOSModalHandler;

  const [chapterId, setChapterId] = useState<string | null>(null);
  const [losId, setLOSId] = useState<string | null>(null);

  const hasLevelItem = useMemo(() => {
    return qBankStructureLevelItems.some((levelItem) => levelItem.qBankStructureLevel.level !== 1);
  }, [qBankStructureLevelItems]);

  const searchLOSVariables = useMemo(() => {
    return {
      order: {
        field: 'updatedAt',
        type: OrderType.Asc,
      },
      paging: {
        currentPage: 0,
        pageSize: 9999, // NOTE: this is a workaround to get all LOS by estimation
      },
      search: {
        qBankStructureId: qBankStructureId,
      },
    };
  }, [qBankStructureId]);

  const {
    data: searchLOSResult,
    loading: isSearchingLOS,
    refetch: refetchSearchLOS,
  } = useSearchLearningObjectiveStatementsQuery({
    skip: !hasLevelItem || !qBankStructureId,
    variables: searchLOSVariables,
    notifyOnNetworkStatusChange: true,
  });

  const [deleteLOS] = useDeleteLearningObjectiveStatementsMutation();

  const searchLOSData = useMemo(() => {
    return searchLOSResult?.qbsSearchLearningObjectiveStatements.learningObjectiveStatements;
  }, [searchLOSResult]);

  const levelItemsWithLOS = useMemo(() => {
    if (qBankStructureLevelItems && searchLOSData) {
      const los = searchLOSData;
      const nestedLevelItems = getLevelItemsForLOS(qBankStructureLevelItems, los);
      return nestedLevelItems;
    } else {
      return [];
    }
  }, [qBankStructureLevelItems, searchLOSResult]);

  const hasDisabledLOS = useMemo(() => {
    return searchLOSData?.some((los) => !los.enabled);
  }, [searchLOSData]);

  const questionCountByLOSId = useMemo(() => {
    return new Map(
      searchLOSData?.map((los) => [
        los.id,
        (los.activeQuestionCount?.single ?? 0) + (los.activeQuestionCount?.itemSet ?? 0),
      ]),
    );
  }, [searchLOSData]);

  const handleDeleteLOS = async () => {
    const los = searchLOSData?.find((item) => item.id === losId);

    if (!los) {
      return;
    }

    await deleteLOS({
      variables: {
        input: {
          qBankStructureId: qBankStructureId,
          chapterLevelItemId: los?.qBankStructureLevelItemId ?? '',
          learningObjectiveStatementId: los?.id ?? '',
        },
      },
      refetchQueries: [
        {
          query: SearchLearningObjectiveStatementsDocument,
          variables: searchLOSVariables,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        closeDeleteLOSModal();
        showSnackbar({
          message: 'LOS has been deleted',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'Unable to Delete LOS',
          variant: 'error',
        });
      },
    });
  };

  const handleRefetchSearchLOS = async () => {
    await refetchSearchLOS();
  };

  useEffect(() => {
    handleRefetchSearchLOS();
  }, [qBankStructureInformation, qBankStructureLevelItems]);

  if (isSearchingLOS) {
    return <Loading />;
  }

  if (!hasLevelItem || levelItemsWithLOS.length <= 0) {
    return (
      <NoRowsOverlay
        title='ไม่พบวัตถุประสงค์การเรียนรู้'
        description='กรุณาสร้างโครงสร้างหลักสูตรก่อนเพื่อดำเนินการต่อ'
        icon={DescriptionOutlinedIcon}
      />
    );
  }

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      {hasDisabledLOS ? (
        <Alert severity='warning'>
          วัตถุประสงค์การเรียนรู้ (LOS) ที่เพิ่มใหม่ จะยังไม่สามารถใช้งานได้จนกว่าจะ Publish
          คลังข้อสอบอีกครั้ง
        </Alert>
      ) : null}

      {levelItemsWithLOS.map((levelItem) => {
        return (
          <LOSCollapse
            key={levelItem.id}
            data={levelItem}
            status={status}
            qBankStructureLevelItems={qBankStructureLevelItems}
            qBankStructureInformationCode={qBankStructureInformation.code}
            onAddLOS={(_, value) => {
              openAddLOSModal();
              setChapterId(value);
            }}
            slots={{
              losList: {
                onRowClick: (losId) => {
                  navigate('/qbank-structures/:structureId/los/:losId/questions', {
                    params: { structureId: qBankStructureId, losId },
                  });
                },
                endAdornment: ({ losId, status }) => {
                  const questionCount = questionCountByLOSId.get(losId) ?? 0;

                  return (
                    <Flex
                      sx={(theme) => ({
                        px: theme.spacing(2),
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                      })}
                    >
                      {status !== LearningObjectiveStatementStatus.Disabled ? (
                        <LOSQuestionCountChip count={questionCount} />
                      ) : null}
                      <Flex onClick={(e) => e.stopPropagation()}>
                        <LOSListRowActionMenu
                          losId={losId}
                          status={status}
                          questionCount={questionCount}
                          onEdit={(losId) => {
                            setLOSId(losId);
                            openEditLOSModal();
                          }}
                          onDelete={(losId) => {
                            setLOSId(losId);
                            openDeleteLOSModal();
                          }}
                          onDisable={(losId) => {
                            setLOSId(losId);
                            openDisableLOSModal();
                          }}
                        />
                      </Flex>
                    </Flex>
                  );
                },
              },
            }}
          />
        );
      })}
      <AddLOSFormModal chapterId={chapterId} open={isAddLOSModalOpen} onClose={closeAddLOSModal} />
      <EditLOSFormModal losId={losId} open={isEditLOSModalOpen} onClose={closeEditLOSModal} />
      <DisableLOSModal losId={losId} open={isDisableLOSModalOpen} onClose={closeDisableLOSModal} />
      <ConfirmationModal
        title='Delete LOS?'
        content='คุณต้องการลบวัตถุประสงค์การเรียนรู้นี้หรือไม่'
        acceptText='Delete LOS'
        open={isDeleteLOSModalOpen}
        onAccept={handleDeleteLOS}
        onDiscard={closeDeleteLOSModal}
        onClose={closeDeleteLOSModal}
      />
    </Flex>
  );
}
